import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { articleApi, type ArticleUnitSummary } from "../api/articleUnits";
import type { DocStatus } from "../api/types";
import { useAuthOptional } from "../auth/AuthContext";
import { ArticleRowMenu } from "../components/ArticleRowMenu";
import { ArticleUnitRow } from "../components/ArticleUnitRow";
import { DeleteArticleDialog } from "../components/DeleteArticleDialog";
import { MergeArticlesDialog } from "../components/MergeArticlesDialog";
import { NewArticleDialog } from "../components/NewArticleDialog";
import { RenameArticleDialog } from "../components/RenameArticleDialog";
import { Button } from "../compose/controls";
import {
  SegmentedControl,
  type SegmentedOption,
} from "@norkron/web-segmented-control";
import { Inbox, LibraryBig, Plus } from "../compose/Icon";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import { useToast } from "../compose/Toast";
import { canInWorkspace, isMemberOf } from "../lib/roles";
import { useAsync } from "../lib/useAsync";

const STATUS_SEGMENTS: SegmentedOption<DocStatus | "all">[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "delivered", label: "Delivered" },
  { value: "superseded", label: "Superseded" },
];

/**
 * Articles landing `/w/:slug` (article-unit pivot §5) — the ARTICLE-FIRST home of
 * a workspace. Lists every article unit ({ brief, research, article }) as a card
 * linking straight into `<ArticleWorkbench>` at `/w/:slug/article/:id`. Reuses the
 * `.doclist`/`.docrow` chrome + `<StatusBadge>` + status `<SegmentedControl>` from
 * the document Library (RULE ZERO — one list look), and keeps the legacy document
 * Library one click away in the header + the shell nav.
 */
export function Articles() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") as DocStatus | "all") ?? "all";
  const navigate = useNavigate();
  const toast = useToast();

  // WHO may start an article: MEMBERSHIP only — the literal reading of "allow
  // anyone to create a new article project", and the same gate
  // `api.upload_service.upload()` already applies to handing in a briefing ("any
  // role — clients upload briefings"). WRITING the brief in the editor is a
  // separate, stricter question: that save goes through
  // `POST /api/documents/:id/content`, which enforces `edit_doc`.
  const auth = useAuthOptional();
  const isStaff = !!auth?.user?.is_staff;
  const memberships = auth?.memberships ?? [];
  const canCreate = isMemberOf(isStaff, memberships, slug);
  const canWriteInEditor = canInWorkspace(isStaff, memberships, slug, "edit_doc");

  // The lifecycle affordances the per-row menu offers, gated exactly as
  // `api/article_admin_service.zig` gates the terminals behind them: rename and
  // take-parts on `edit_doc`, delete on `admin_workspace` (an editor who may
  // freely save may not dissolve the project the saves live in). The server is
  // authoritative; this only decides what to draw.
  const canAdminWorkspace = canInWorkspace(
    isStaff,
    memberships,
    slug,
    "admin_workspace",
  );

  const [createOpen, setCreateOpen] = useState(false);
  /** Which lifecycle dialog is open, and for which project. One at a time. */
  const [acting, setActing] = useState<{
    kind: "rename" | "delete" | "merge";
    unit: ArticleUnitSummary;
  } | null>(null);

  const units = useAsync(() => articleApi.listArticleUnits(slug), [slug]);

  const setStatus = (next: DocStatus | "all") => {
    const p = new URLSearchParams(params);
    if (next === "all") p.delete("status");
    else p.set("status", next);
    setParams(p, { replace: true });
  };

  const visible = (units.data ?? []).filter(
    (u) => status === "all" || u.status === status,
  );

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Articles</h1>
          <p className="page__sub">
            Every article unit — its brief, research and the article itself, in one
            review workbench.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Link
            to={`/w/${slug}/library`}
            className="btn btn--ghost"
            data-testid="go-to-library"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <LibraryBig size={15} strokeWidth={1.5} />
            Document library
          </Link>
          <Button
            variant="primary"
            data-testid="new-article"
            disabled={!canCreate}
            // A disabled control that says nothing is a dead end. `title` carries
            // the REASON, and `aria-describedby` gives a screen reader the same
            // sentence rather than a silently inert button.
            title={
              canCreate
                ? "Start a new article project"
                : "You're not a member of this workspace, so you can't start an article here."
            }
            aria-describedby={canCreate ? undefined : "new-article-why"}
            icon={<Plus size={15} strokeWidth={1.5} />}
            onClick={() => setCreateOpen(true)}
          >
            New article
          </Button>
          {canCreate ? null : (
            <span hidden id="new-article-why" data-testid="new-article-why">
              You're not a member of this workspace, so you can't start an article
              here.
            </span>
          )}
        </div>
      </div>

      <div className="docbar">
        <SegmentedControl
          ariaLabel="Filter by status"
          value={status}
          options={STATUS_SEGMENTS}
          segmentTestIdPrefix="filter"
          onChange={setStatus}
        />
        <span className="page__sub" style={{ margin: 0 }}>
          {units.data
            ? `${visible.length} article${visible.length === 1 ? "" : "s"}`
            : ""}
        </span>
      </div>

      {units.loading ? (
        <div className="doclist">
          <SkeletonRows rows={5} />
        </div>
      ) : units.error ? (
        <ErrorState body="We couldn't load the article units for this workspace." />
      ) : visible.length > 0 ? (
        <div className="doclist" data-testid="article-list">
          {visible.map((u) => (
            <ArticleUnitRow
              key={u.id}
              slug={slug}
              unit={u}
              actions={
                <ArticleRowMenu
                  unitId={u.id}
                  canEdit={canWriteInEditor}
                  canAdmin={canAdminWorkspace}
                  onRename={() => setActing({ kind: "rename", unit: u })}
                  onMerge={() => setActing({ kind: "merge", unit: u })}
                  onDelete={() => setActing({ kind: "delete", unit: u })}
                />
              }
            />
          ))}
        </div>
      ) : (
        <div className="doclist">
          <EmptyState
            icon={<Inbox size={30} strokeWidth={1.5} />}
            title="No article units yet"
            body={
              canCreate
                ? "No article unit matches this filter. Clear it, or start one with New article — upload a brief, paste it, or write it here."
                : "No article unit matches this filter. Clear it, or open the document library."
            }
          />
        </div>
      )}

      {units.error ? (
        <div style={{ marginTop: 12 }}>
          <Button variant="ghost" onClick={units.reload} data-testid="articles-retry">
            Retry
          </Button>
        </div>
      ) : null}

      {createOpen ? (
        <NewArticleDialog
          slug={slug}
          canWriteInEditor={canWriteInEditor}
          onClose={() => setCreateOpen(false)}
          onCreated={(unit, { openEditor }) => {
            setCreateOpen(false);
            toast.show(`Created “${unit.title}”`);
            units.reload();
            // Land in the project. `show=brief` opens the brief pane; `edit=brief`
            // additionally enters the REAL editor, which is what "write it
            // directly in the editor" has to mean — anything less drops the user
            // on a read view of an empty document.
            navigate(
              `/w/${slug}/article/${unit.id}?show=brief${
                openEditor ? "&edit=brief" : ""
              }`,
            );
          }}
        />
      ) : null}

      {acting?.kind === "rename" ? (
        <RenameArticleDialog
          unit={acting.unit}
          onClose={() => setActing(null)}
          onRenamed={(updated) => {
            setActing(null);
            toast.show(`Renamed to “${updated.title}”`);
            units.reload();
          }}
        />
      ) : null}

      {acting?.kind === "delete" ? (
        <DeleteArticleDialog
          unit={acting.unit}
          onClose={() => setActing(null)}
          onDeleted={(plan) => {
            setActing(null);
            // The SERVER's own counts — what it did, not what we asked for.
            toast.show(
              plan.documents_archived > 0
                ? `Deleted the project · ${plan.documents_archived} document${
                    plan.documents_archived === 1 ? "" : "s"
                  } archived (restorable)`
                : `Deleted the project · ${plan.documents_detached} document${
                    plan.documents_detached === 1 ? "" : "s"
                  } kept in the library`,
            );
            units.reload();
          }}
        />
      ) : null}

      {acting?.kind === "merge" ? (
        <MergeArticlesDialog
          unit={acting.unit}
          onClose={() => setActing(null)}
          onMerged={(result) => {
            setActing(null);
            toast.show(
              `Took ${result.parts.length} part${
                result.parts.length === 1 ? "" : "s"
              } into “${result.unit?.title ?? acting.unit.title}”`,
            );
            units.reload();
          }}
        />
      ) : null}
    </div>
  );
}
